import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import _ from "lodash";
import React from "react";
import { CustomFormInput } from "src/components/Inputs/CustomFormInput";
import { Card } from "src/components/Layout/Card";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useEditFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/context";
import {
  FormType,
  getOptionIdsFormValue,
  getQuestionTypeFormValue,
  isInvalid,
  Option,
  toOptionDraft,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/formik";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/draft";
import * as Question from "src/services/formTemplate/question";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { RelatedQuestionVisibility } from "../../QuestionVisibility/QuestionVisibility";
import { SchoolItem } from "../types";
import { AdditionalQuestionsCheckboxCard } from "./AdditionalQuestionsCheckboxCard";
import { EligibilityCheckboxCard } from "./EligibilityCheckboxCard";

type Props = {
  sectionType: AF.SectionType;
  questionId: uuid;
  question: Draft.Question | undefined;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  showAdditionalQuestions?: boolean;
  verificationOptions: AF.FormVerification<AF.WithId>[];
  otherQuestions: readonly Draft.Question[];
};
export const AnswersOptions: React.FC<Props> = ({
  sectionType,
  questionId,
  schools,
  showAdditionalQuestions = true,
  verificationOptions,
  question,
  otherQuestions,
}) => {
  const form = useFormikContext<FormType>();
  const questionType = getQuestionTypeFormValue(questionId, form.values);
  const optionIds = getOptionIdsFormValue(questionId, form.values);

  const { formTemplateVersion } = useEditFormTemplateContext();
  const isV2FormTemplate = formTemplateVersion === "2.0.0";

  const addAnswerClickHandler = React.useCallback(() => {
    const optionId = window.crypto.randomUUID();
    form.setValues((values) => ({
      ...values,
      optionIds: {
        ...values.optionIds,
        [questionId]: [...optionIds, optionId],
      },
      options: {
        ...values.options,
        [optionId]: {
          label: "",
          isNew: true,
        },
      },
    }));
  }, [form, optionIds, questionId]);

  const onRemoveAnswerHandler = React.useCallback(
    (optionIdToRemove: uuid) => {
      const newList = optionIds.filter(
        (optionId) => optionId !== optionIdToRemove
      );
      form.setValues((values) => ({
        ...values,
        optionIds: { ...values.optionIds, [questionId]: newList },
        options: _.omit(values.options, optionIdToRemove),
      }));
    },
    [form, optionIds, questionId]
  );

  const onValueChangeHandler = React.useCallback(
    (optionId: uuid, option: Option) => {
      return (value: string) => {
        form.setTouched({
          ...form.touched,
          options: {
            ...form.touched.options,
            [optionId]: {
              ...form.touched.options?.[optionId],
              value: true,
            },
          },
        });

        const updatedOption = { ...option, value };
        form.setValues((values) => ({
          ...values,
          options: {
            ...values.options,
            [optionId]: updatedOption,
          },
        }));
      };
    },
    [form]
  );

  const onLabelChangeHandler = React.useCallback(
    (optionId: uuid, option: Option) => {
      return (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setTouched({
          ...form.touched,
          options: {
            ...form.touched.options,
            [optionId]: {
              ...form.touched.options?.[optionId],
              label: true,
            },
          },
        });
        const updatedOption = { ...option, label: event.target.value };
        form.setValues((values) => ({
          ...values,
          options: {
            ...values.options,
            [optionId]: updatedOption,
          },
        }));
      };
    },
    [form]
  );

  const Confirmation = useConfirmationDialog({
    body: "",
    header: "Edit answer values?",
  });

  if (!Question.hasOptions({ type: questionType })) {
    return null;
  }

  const touched = form.touched.validations?.[questionId]?.options;
  const error = form.errors.validations?.[questionId]?.options;

  return (
    <Flex direction="column" gap="3" alignItems="flex-start">
      {optionIds.map((optionId, index) => {
        const originalOption = Question.hasOptions(question)
          ? question.options.find((o) => o.id === optionId)
          : undefined;
        const option: Draft.Option = toOptionDraft(
          optionId,
          form.values,
          originalOption
        );

        const labelTouched = form.touched.options?.[optionId]?.label;
        const labelError = form.errors.options?.[optionId]?.label;

        const valueTouched = form.touched.options?.[optionId]?.value;
        const valueError = form.errors.options?.[optionId]?.value;

        return (
          <Card key={optionId} showBorder useFlex width="100%">
            <Flex gap="2" direction="row" alignItems="flex-start">
              <CustomFormInput
                id={`${optionId}.label`}
                label={`Answer ${index + 1}`}
                error={labelTouched ? labelError : undefined}
              >
                <Input
                  id={`${optionId}.label`}
                  value={option.label}
                  flexGrow="1"
                  onChange={onLabelChangeHandler(optionId, option)}
                />
              </CustomFormInput>

              <CustomFormInput
                error={valueTouched ? valueError : undefined}
                id={`${optionId}.value`}
                label={
                  isV2FormTemplate ? "Answer value" : "Answer value (optional)"
                }
              >
                <Input
                  id={`${optionId}.value`}
                  flexGrow={1}
                  onChange={async (event) => {
                    const inputElement = event.target as HTMLInputElement;
                    const value = inputElement.value;
                    onValueChangeHandler(optionId, option)(value);
                    return;
                  }}
                  placeholder="E.g.: RED"
                  value={option.value ?? ""}
                />
              </CustomFormInput>

              {option.isNew && (
                <Button
                  flexShrink={0}
                  variant="outline"
                  colorScheme="gray"
                  onClick={async () => {
                    if (onRemoveAnswerHandler) {
                      onRemoveAnswerHandler(optionId);
                    }
                  }}
                >
                  Remove answer
                </Button>
              )}
            </Flex>

            <Flex direction="column" paddingLeft="2rem" gap="3">
              {question?.key !== undefined && option.value ? (
                <Box>
                  <RelatedQuestionVisibility
                    questionId={question.id}
                    answerId={optionId}
                    otherQuestions={otherQuestions}
                  >
                    {(relatedQuestions) => (
                      <>When chosen, reveals {relatedQuestions}</>
                    )}
                  </RelatedQuestionVisibility>
                </Box>
              ) : null}
              <EligibilityCheckboxCard
                sectionType={sectionType}
                questionType={questionType}
                option={option}
                schools={schools}
              />
              {showAdditionalQuestions && (
                <AdditionalQuestionsCheckboxCard
                  sectionType={sectionType}
                  option={option}
                  schools={schools}
                  verificationOptions={verificationOptions}
                />
              )}
            </Flex>
          </Card>
        );
      })}
      <Flex direction="column" alignItems="flex-start">
        <Button colorScheme="gray" onClick={addAnswerClickHandler}>
          Add answer
        </Button>
        <FormControl isInvalid={isInvalid({ touched, error })}>
          <FormErrorMessage>{error}</FormErrorMessage>
        </FormControl>
      </Flex>
      {Confirmation.confirmationDialog}
    </Flex>
  );
};
